import '../../scss/smart-facade.scss';
import fetchUrlToAudioCache from '../service/cache';
import { getLocalStorage, isInViewport } from '../utils/utils';
import sendTrackingEvent from '../service/tracking';
import { handleTrackingId } from '../metrics-utils';
import state from '../store/store';
import TranslateComponent from './components/translate/translate-component';
import { Queued } from '../../types';

const _translate = new TranslateComponent();
let currentLang = _translate.getLang();
const etxDebugName = 'ETX';
const etxDebugFacadeName = 'Facade';
let prefetchObserver: MutationObserver;
const prefetchObservers = [];
initStore();
waitForElm('.etx-player').then((etxPlayer: HTMLElement) => {
  window.addEventListener('offline', () => {
    etxPlayer.dataset.becameOffline = 'true';
    console.log('[ETX][INTERNET] - Became offline');
  });
  window.addEventListener('online', () => {
    etxPlayer.removeAttribute('data-became-offline');
    console.log('[ETX][INTERNET] - Became online');
  });

  const version = '2.2.7';
  etxPlayer.dataset.v = version;

  localStorage.removeItem('etx-queued');
  const etxPlayerIntegrationDataset = etxPlayer.dataset;
  let windowNamespace = window.__ETXPLAYER__?.config;
  const mainColor = windowNamespace?.color?.main || etxPlayerIntegrationDataset.mainColor;
  const secondaryColor = windowNamespace?.color?.secondary || etxPlayerIntegrationDataset.secondaryColor;
  const listenArticleText = windowNamespace?.informationText?.fr?.article || etxPlayerIntegrationDataset.listenArticleText;
  const cssVariableInJsStyle = `
            ${mainColor ? '--mainColor:' + mainColor + ';' : ''}
            ${secondaryColor ? '--secondaryColor:' + secondaryColor + ';' : ''}
            ${listenArticleText ? '--listenArticleText:' + listenArticleText + ';' : ''}
        `
    .replace(/\s\s+/g, '')
    .replace(/:/g, ': ')
    .replace(/;/g, '; ')
    .replace(/ "/g, '"');

  let timerOnScroll;
  document.addEventListener(
    'scroll',
    () => {
      clearTimeout(timerOnScroll);
      timerOnScroll = setTimeout(() => {
        onScrollEvent();
      }, 500);
    },
    {
      passive: true,
    },
  );

  const etxMiniPlayers = document.querySelectorAll('.etx-fplayer');
  const launcherIconCustomClassName = etxPlayerIntegrationDataset.launcherIconCustomClass;

  const miniPlayersClassName = etxPlayerIntegrationDataset.playersCustomClass;

  etxMiniPlayers.forEach(async (miniPlayerElem) => {
    const miniPlayer = miniPlayerElem as HTMLElement;
    const { url } = miniPlayer.dataset;
    if (url) {
      const miniPlayerHTML = getMiniPlayerInnerHTML({ etxPlayerIntegrationDataset });
      fakePlayerPrefetchObserver(miniPlayer);
      miniPlayer.classList.add('etx-facade');
      if (!state.config.apiKey) {
        miniPlayer.classList.add('etx-disable-player');
      }
      if (miniPlayersClassName) {
        miniPlayer.classList.add(miniPlayersClassName);
      }
      miniPlayer.setAttribute('style', cssVariableInJsStyle);
      miniPlayer.insertAdjacentHTML('afterbegin', miniPlayerHTML);
    } else {
      console.log(`[${etxDebugName}][${etxDebugFacadeName}][Error] - Url missing`);
    }
  });
  etxMiniPlayers.forEach((miniPlayerElement: HTMLElement) => {
    const { url } = miniPlayerElement.dataset;
    generateAudiogram(miniPlayerElement);
    const btnPlay = miniPlayerElement.querySelector('.etx-fplayer-btn-play');
    btnPlay.addEventListener('click', onClickShowLoader);
    const btnAddToPlaylist = miniPlayerElement.querySelector('.etx-fplayer-btn-actions');
    btnAddToPlaylist.addEventListener('click', onClickShowLoader);
    if (btnAddToPlaylist && url) {
      disabledAddToPlaylistButtonIfAlreadyInTheLocalStorage(btnAddToPlaylist, url);
    }
  });
  const btnBubble = getBtnBubbleHTML({ launcherIconCustomClassName, cssVariableInJsStyle });
  if (etxMiniPlayers[0]) {
    etxMiniPlayers[0].insertAdjacentHTML('beforebegin', btnBubble);
  } else {
    document.body.insertAdjacentHTML('beforebegin', btnBubble);
  }
  handleTrackingId();
  sendDisplayEvent();
  const btnBubbleElement = document.querySelector('.etx-player-btn-cta-facade');
  if (btnBubbleElement) {
    btnBubbleElement.addEventListener('click', onClickShowLoader);
    const { state: currentState } = getLocalStorage('etx-current-track');
    const isKeepPlaying = currentState === 'playing';
    if (isKeepPlaying) {
      addToQueued({ key: 'shouldOpen', method: 'set', value: true, clickedButton: null });
      destroyFacadeAndLoadSmartPlayer();
    }
  }

  function onScrollEvent() {
    const fakes = document.querySelectorAll('.etx-fplayer');
    fakes.forEach(async (fake: any) => {
      if (isInViewport(fake) && !fake.dataset.prefetch) {
        fake.dataset.prefetch = 'true';
      }
    });
  }

  /**
   * It's an observer that observes the attribute `data-prefetch` of the fake player. When the
   * attribute changes, it checks if the value is `true` or `done`. If it's `true`, it calls the
   * function `fetchUrlOnFplayerVisible` with the fake player as argument. If it's `done`, it checks if
   * there are loaders inside the fake player and if there are, it calls the function
   * `checkIfNeedToContinueActionFromCurrentPrefetch` with the fake player and the loader as arguments
   * @param fake - the fake player element
   */
  function fakePlayerPrefetchObserver(fake) {
    prefetchObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const fakePlayer: any = mutation.target;
        if (mutation.type === 'attributes') {
          const { prefetch } = fakePlayer.dataset;
          if (prefetch === 'true') {
            fetchUrlOnFplayerVisible(fakePlayer);
          } else if (prefetch === 'failed') {
            const btnAddToPlaylist = fake.querySelector('.etx-fplayer-btn-actions');
            if (btnAddToPlaylist) {
              btnAddToPlaylist.remove();
            }
          }
        }
      });
    });
    prefetchObserver.observe(fake, {
      attributes: true,
      attributeFilter: ['data-prefetch'],
    });
    prefetchObservers.push(prefetchObserver);
    if (isInViewport(fake)) {
      fake.dataset.prefetch = 'true';
    }
  }

  /**
   * It fetches the audio url from the server and updates the fake element with the audio url
   * @param fake - the fake element that is being displayed
   * @returns The dataset object is being returned.
   */
  async function fetchUrlOnFplayerVisible(fake) {
    const { url, audiourl, prefetch } = fake.dataset;
    if (url && !audiourl && prefetch === 'true') {
      try {
        const dataset = await convertUrlToAudio({
          url,
          version: '',
          fake,
        });
        if (dataset && dataset.status && dataset.status === 'success') {
          fake.dataset.prefetch = 'done';
          return dataset;
        }
        if (dataset && dataset.status && dataset.status === 'failed') {
          fake.dataset.prefetch = 'failed';
          throw new Error('Audioization failed');
        }
      } catch (err) {
        fake.dataset.prefetch = 'error';
        throw new Error('No data to display, probably an error' + err);
      }
    }
  }

  /**
   * It takes a url, sends it to the media api, and returns the audio url, title, image url, and
   * publication date
   * @param [params] - { url, version: '', fake }
   * @returns The data object is being returned.
   */
  async function convertUrlToAudio(params = { url: '', version: '', fake: null }) {
    const { url, version: currentVersion, fake } = params;
    windowNamespace = window.__ETXPLAYER__?.config;
    const apiKey = windowNamespace.apiKey || etxPlayer.dataset.apiKey;
    let payload = null;
    let urlToFetch = '';
    if (!apiKey) {
      payload = {
        method: 'get',
      };
      urlToFetch = `https://sybclo6bn4v3cggr5vdz6iygg40gciwd.lambda-url.eu-west-1.on.aws/?url=${url}`;
    } else {
      payload = {
        body: JSON.stringify({ url, currentVersion }),
        method: 'post',
        headers: new Headers({
          'x-api-key': apiKey,
        }),
      };
      urlToFetch = `${process.env.MEDIA_API_HOST}/url-to-audio`;
    }
    const data = await fetchUrlToAudioCache(urlToFetch, payload);
    if (!data) {
      return null;
    }
    if (!state.config.apiKey) {
      fake.classList.remove('etx-disable-player');
    }
    if (data.output) {
      fake.setAttribute('data-audiourl', data.output);
    }
    if (data.title) {
      fake.setAttribute('data-title', data.title);
    }
    if (data.imageUrl) {
      fake.setAttribute('data-imageurl', data.imageUrl);
    }
    if (data.created_at) {
      fake.setAttribute('data-publicationdate', data.created_at);
    }
    if (data.id) {
      fake.setAttribute('data-r', data.id);
    }
    return data;
  }
});
/**
 * It takes an object with three properties as an argument, and if the isLoading property is true,
 * it appends the loaderWrapper property to the playPauseElement property, disables the
 * playPauseElement, and adds a class to it. If isLoading is false, it removes the loaderWrapper
 */
function setLoadingState({ playPauseElement, isLoading, loaderWrapper }) {
  const element = playPauseElement;
  if (isLoading) {
    element.innerHTML = '';
    element.appendChild(loaderWrapper);
    element.setAttribute('disabled', String(true));
    element.classList.add('etx-no-hover');
  }

  if (!isLoading) {
    loaderWrapper.remove();
  }
}

/**
 * Wait for the element to be added to the DOM, then return it.
 * @param selector - The CSS selector to wait for.
 * @returns A promise that resolves to the first element that matches the selector.
 */
function waitForElm(selector) {
  return new Promise((resolve) => {
    if (document && document.querySelector(selector)) {
      resolve(document.querySelector(selector));
      return;
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
}

/**
 * It returns a string of HTML code that will be used to create the bubble
 * @returns A string of HTML code.
 */
function getBtnBubbleHTML({ launcherIconCustomClassName, cssVariableInJsStyle }) {
  return `
        <div class="etx-facade-bubble ${launcherIconCustomClassName || ''}" style="${cssVariableInJsStyle}">
    
        <button type="button" class="etx-player-btn-cta-facade" title="Agrandir le lecteur">
            <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2MDkuMTcgNDA2LjE2Ij48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6I2ZmZjt9LmNscy0xLC5jbHMtMntzdHJva2U6I2ZmZjtzdHJva2UtbWl0ZXJsaW1pdDoxMDt9LmNscy0ye2ZpbGw6bm9uZTtzdHJva2Utd2lkdGg6MjhweDt9PC9zdHlsZT48L2RlZnM+PGcgaWQ9IkNhbHF1ZV8yIiBkYXRhLW5hbWU9IkNhbHF1ZSAyIj48ZyBpZD0iQ2FscXVlXzEtMiIgZGF0YS1uYW1lPSJDYWxxdWUgMSI+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNTAuOSwyMDVINzcuNGEwLDAsMCwwLDEsMCwwVjQwNS42NmEwLDAsMCwwLDEsMCwwSDUwLjlBNTAuNCw1MC40LDAsMCwxLC41LDM1NS4yNlYyNTUuNEE1MC40LDUwLjQsMCwwLDEsNTAuOSwyMDVaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNTgyLjE3LDIwNWgyNi41YTAsMCwwLDAsMSwwLDBWNDA1LjY2YTAsMCwwLDAsMSwwLDBoLTI2LjVhNTAuNCw1MC40LDAsMCwxLTUwLjQtNTAuNFYyNTUuNEE1MC40LDUwLjQsMCwwLDEsNTgyLjE3LDIwNVoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDExNDAuNDQgNjEwLjY2KSByb3RhdGUoMTgwKSIvPjxwYXRoIGNsYXNzPSJjbHMtMiIgZD0iTTExMC45NCw0MDUuNjZ2LTE5OGMwLTEwNyw4Ni43LTE5My42NCwxOTMuNjQtMTkzLjY0czE5My42NSw4Ni42OCwxOTMuNjUsMTkzLjY0djE5OCIvPjwvZz48L2c+PC9zdmc+" class="icon-white">
        </button>
        </div>
    `;
}

/**
 * It returns a string of HTML code that will be used to create the mini player
 * @returns A string of HTML code.
 */
function getMiniPlayerInnerHTML({ etxPlayerIntegrationDataset }) {
  currentLang = state.config.lang;
  return `
            <div class="etx-fplayer-trademark"><a class="etx-hidden-link" target="_blank" href="https://hubs.ly/Q010J6xZ0">Powered by <strong>ETX Studio</strong></a></div>
            <div class="etx-fplayer-player etx-facade">
                <div class="etx-fplayer-player-outer">
                    <div class="etx-fplayer-container">
                        <button type="button" class="etx-fplayer-btn-play" title="Écouter">
                            <!--<div class="icon-play"></div>-->
                        </button>
                        <div class="etx-fplayer-label-listen">${state.config.informationText[currentLang].article}</div>
                    </div>
                    <canvas class="etx-fplayer-audiogram" style="width: 100%; height: 100%;"></canvas>
                </div>
                <button type="button" class="etx-fplayer-btn-actions" title="Ajouter à la playlist">
                    <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI5Ny44MDUiIGhlaWdodD0iNjUuNDkyIiB2aWV3Qm94PSIwIDAgOTcuODA1IDY1LjQ5MiI+CiAgPGcgaWQ9Ikdyb3VwZV8xMDYiIGRhdGEtbmFtZT0iR3JvdXBlIDEwNiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTIxNTguODI2IC0xMjE4LjU4MikiPgogICAgPHBhdGggaWQ9IlRyYWPDqV85NiIgZGF0YS1uYW1lPSJUcmFjw6kgOTYiIGQ9Ik0wLDBINTQuNjIxIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgyMjU0LjEzMSAxMjIxLjA4Mikgcm90YXRlKC0xODApIiBzdHJva2U9IiNmZmYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSI1Ii8+CiAgICA8cGF0aCBpZD0iVHJhY8OpXzk3IiBkYXRhLW5hbWU9IlRyYWPDqSA5NyIgZD0iTTAsMEgzNi44OTIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIyNTQuMTMxIDEyNDEuMjQ2KSByb3RhdGUoLTE4MCkiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2Utd2lkdGg9IjUiLz4KICAgIDxwYXRoIGlkPSJUcmFjw6lfOTgiIGRhdGEtbmFtZT0iVHJhY8OpIDk4IiBkPSJNMCwwSDU4Ljg3NyIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjI1NC4xMzEgMTI2MS40MSkgcm90YXRlKC0xODApIiBzdHJva2U9IiNmZmYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSI1Ii8+CiAgICA8cGF0aCBpZD0iVHJhY8OpXzk5IiBkYXRhLW5hbWU9IlRyYWPDqSA5OSIgZD0iTTAsMEg3My43ODQiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIyNTQuMTMxIDEyODEuNTc0KSByb3RhdGUoLTE4MCkiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2Utd2lkdGg9IjUiLz4KICAgIDxnIGlkPSJHcm91cGVfMTAzIiBkYXRhLW5hbWU9Ikdyb3VwZSAxMDMiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDE0ODAuODQ4IDE5MTcuMzY3KSByb3RhdGUoLTQ1KSI+CiAgICAgIDxnIGlkPSJHcm91cGVfMzIiIGRhdGEtbmFtZT0iR3JvdXBlIDMyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg5NjAuMDk5IDIuMjcyKSI+CiAgICAgICAgPHBhdGggaWQ9IlRyYWPDqV80NiIgZGF0YS1uYW1lPSJUcmFjw6kgNDYiIGQ9Ik0uMzI2LDAsMCwzOCIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTAuMjMgMC4yMzEpIHJvdGF0ZSgtNDUpIiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmYiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSI1Ii8+CiAgICAgICAgPHBhdGggaWQ9IlRyYWPDqV80NyIgZGF0YS1uYW1lPSJUcmFjw6kgNDciIGQ9Ik0wLDAsLjMyNiwzOCIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjYuNjQgMCkgcm90YXRlKDQ1KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmZmIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS13aWR0aD0iNSIvPgogICAgICA8L2c+CiAgICA8L2c+CiAgPC9nPgo8L3N2Zz4K" class="icon-white" style="pointer-events: none">
                </button>

            </div>
    `;
}

/**
 * > The function is called when a user clicks on a play button, a bubble button or an 'add to
 * playlist' button. It adds the clicked button to a queue and sets the loading state of the button
 * @param e - the event object
 */
function onClickShowLoader(e) {
  const clickedButton = e.target;
  if (clickedButton) {
    console.log(`[${etxDebugName}][${etxDebugFacadeName}] - Should show loader`);
    const loaderHTML = '<div class="lds-ring-v1" ><div ></div><div ></div><div ></div><div ></div></div>';
    let loaderWrapper = document.createElement('div');
    loaderWrapper.classList.add('etx-loader-wrapper');
    loaderWrapper.innerHTML = loaderHTML;
    const isBtnPlay = clickedButton.classList.contains('etx-fplayer-btn-play');
    const isBtnBubble = clickedButton.classList.contains('etx-player-btn-cta-facade');
    const isBtnAction = clickedButton.classList.contains('etx-fplayer-btn-actions');
    const playPauseImageElement = clickedButton.querySelector('img');
    setLoadingState({ playPauseElement: clickedButton, loaderWrapper, isLoading: true });
    if (isBtnPlay) {
      const allFacadePlayButtons = document.querySelectorAll('.etx-fplayer.etx-facade .etx-fplayer-btn-play');
      allFacadePlayButtons.forEach((playButton: HTMLElement) => {
        console.log(`[${etxDebugName}][${etxDebugFacadeName}] - Resetting other play buttons`);

        if (playButton !== clickedButton) {
          playButton.style.borderColor = 'transparent transparent transparent white';
          playButton.classList.remove('etx-no-hover');
          playButton.removeAttribute('disabled');
          loaderWrapper = playButton.querySelector('.etx-loader-wrapper');
          if (loaderWrapper) {
            loaderWrapper.remove();
          }
        }
      });
    }
    if (isBtnAction) {
      // if 'add to playlist' button
      playPauseImageElement.style.opacity = '0';
      addToQueued({ key: 'shouldAddToPlaylist', method: 'push', clickedButton });
      console.log(`[${etxDebugName}][${etxDebugFacadeName}] - Click on a 'Add to Playlist' button`);
      destroyFacadeAndLoadSmartPlayer();
    } else if (isBtnPlay) {
      addToQueued({ key: 'shouldPlay', method: 'set', clickedButton });
      clickedButton.style.borderColor = 'transparent';
      console.log(`[${etxDebugName}][${etxDebugFacadeName}] - Click on a play button`);
      destroyFacadeAndLoadSmartPlayer();
    } else if (isBtnBubble) {
      addToQueued({ key: 'shouldOpen', method: 'set', value: true, clickedButton });
      console.log(`[${etxDebugName}][${etxDebugFacadeName}] - Click on the Bubble button`);
      destroyFacadeAndLoadSmartPlayer();
    }
  } else {
    console.log(`[${etxDebugName}][${etxDebugFacadeName}][Error] - The clicked button element is missing`);
  }
}

/**
 * It takes an object with a key, method, value, and clickedPlayButton, and then it sets the value
 * to the localStorage
 */
function addToQueued({ key, method, value = null, clickedButton }) {
  const etxQueuedEmptyData = { shouldPlay: '', shouldAddToPlaylist: [], shouldOpen: false };
  const etxQueuedLocalStorage = JSON.parse(localStorage.getItem('etx-queued')) || etxQueuedEmptyData;
  const closestMiniPlayer = clickedButton ? clickedButton.closest('.etx-fplayer.etx-facade') : '';
  const url = closestMiniPlayer && closestMiniPlayer.dataset.url ? closestMiniPlayer.dataset.url : '';
  const valueToSet = value || url;
  if (valueToSet != null && key in etxQueuedLocalStorage) {
    if (method === 'set') {
      etxQueuedLocalStorage[key] = valueToSet;
    } else if (method === 'push') {
      etxQueuedLocalStorage[key].push(valueToSet);
    }
    localStorage.setItem('etx-queued', JSON.stringify(etxQueuedLocalStorage));
  }
}

/**
 * It checks if the article is already in the local storage
 * @param articleUrl - the url of the article you want to add to the playlist
 * @returns A boolean value.
 */
function isAlreadyInLocalStorage(articleUrl) {
  const localPlaylist = JSON.parse(localStorage.getItem('etx-playlist')) || [];
  const alreadyThere = localPlaylist.find((local) => local.url === articleUrl);
  return alreadyThere;
}

/**
 * If the url is already in the local storage, then disable the button
 * @param btnAddToPlaylist - The button that will be disabled if the url is already in the local
 * storage.
 * @param url - the url of the media
 */
function disabledAddToPlaylistButtonIfAlreadyInTheLocalStorage(btnAddToPlaylist, url) {
  const isInLocalStorage = isAlreadyInLocalStorage(url);
  if (isInLocalStorage) {
    btnAddToPlaylist.setAttribute('disabled', String(true));
  }
}

/**
 * It draws a canvas element with a random height and width
 * @param fake - the fake player element
 * @param refresh - boolean, if true, the canvas will be cleared and redrawn
 * @returns the value of the variable `fake`
 */
function generateAudiogram(fake, refresh?) {
  if (!fake) return;
  const audiogramElement = fake.querySelector('.etx-fplayer-audiogram');
  if (audiogramElement) {
    if (refresh) {
      // code something here if asked by PO
    }
    const clientRect = audiogramElement.getBoundingClientRect();
    const { width, height } = clientRect;
    audiogramElement.style.width = width;
    audiogramElement.style.minWidth = width;
    setCanvasSize(audiogramElement, width, height);
    const barWidth = 2;
    const spaceBetween = 6;
    const mandatorySpace = barWidth + spaceBetween;
    const maxIteration = width / mandatorySpace;
    for (let index = 0; index < maxIteration - 1; index++) {
      drawAudiogram({
        canvas: audiogramElement,
        maxWidth: width,
        maxHeight: height,
        lastPosition: index * mandatorySpace,
        barWidth,
      });
    }
  }
}

/**
 * It sets the canvas size to the given width and height
 * @param canvas - The canvas element to resize.
 * @param width - The width of the canvas in pixels.
 * @param height - The height of the canvas in pixels.
 */
function setCanvasSize(canvas, width, height) {
  const { style } = canvas;

  style.width = '' + width + 'px';
  style.height = '100%';

  canvas.width = Number(width);
  canvas.height = Number(height);
}

/**
 * It draws a rectangle on the canvas, with a random height, and a width of 1px
 * @param [params] - {
 */
function drawAudiogram(params) {
  const { canvas, maxHeight, lastPosition, barWidth } = params;
  if (canvas?.getContext) {
    const base = maxHeight / 2;
    const randomHeight = getRandomArbitrary(5, base);
    const startHeight = base - randomHeight;
    const endHeight = randomHeight * 2;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.fillStyle = '#FFF';
    ctx.fillRect(lastPosition, startHeight, barWidth, endHeight);
    ctx.stroke();
  }
}

/**
 * It returns a random number between the min and max values
 * @param min - The minimum number you want to generate.
 * @param max - The maximum number you want to generate.
 * @returns A random number between min and max.
 */
function getRandomArbitrary(min, max) {
  return Math.random() * (max - min) + min;
}

function sendDisplayEvent() {
  sendTrackingEvent({ eventType: 'display', eventValue: true });
}

function destroyFacadeAndLoadSmartPlayer() {
  removeFacadesEventListeners();
  import(
    /* webpackChunkName: "mainsmartplayer" */
    /* webpackMode: "lazy" */
    './embed-player'
  );
}

function removeFacadesEventListeners() {
  const etxMiniPlayers = document.querySelectorAll('.etx-fplayer.etx-facade');
  etxMiniPlayers.forEach((miniPlayerElement: HTMLElement) => {
    const btnPlay = miniPlayerElement.querySelector('.etx-facade .etx-fplayer-btn-play');
    btnPlay.removeEventListener('click', onClickShowLoader);
    const btnAddToPlaylist = miniPlayerElement.querySelector('.etx-facade .etx-fplayer-btn-actions');
    btnAddToPlaylist.removeEventListener('click', onClickShowLoader);
  });
  prefetchObservers.forEach((observer) => observer.disconnect());
}

/**
 * It initializes the state of the player
 */
function initStore(): void {
  const etxQueued: Queued = getLocalStorage('etx-queued');
  if (etxQueued) {
    state.queued = etxQueued;
  }
  state.playerContainer = document.querySelector('.etx-player');
  const windowNamespace = window.__ETXPLAYER__?.config;

  state.config.apiKey = windowNamespace?.apiKey;
  state.config.lang = windowNamespace?.lang;
  state.earlyStart = state.playerContainer.dataset.earlyStart === 'true';
  state.config.duration = windowNamespace?.duration;
  // Playlist
  state.config.playlist.enabled = windowNamespace?.playlist?.enabled;
  state.config.playlist.name = windowNamespace?.playlist?.name;
  // Global color
  state.config.color.main = windowNamespace?.color?.main;
  state.config.color.secondary = windowNamespace?.color?.secondary;
  state.config.color.time = windowNamespace?.color?.time;
  state.config.color.mainHover = windowNamespace?.color?.mainHover;
  state.config.color.btnActive = windowNamespace?.color?.btnActive;
  state.config.color.btnHover = windowNamespace?.color?.btnHover;
  // Ads
  state.config.ads.enabled = windowNamespace?.ads?.enabled;
  state.config.ads.channelId = windowNamespace?.ads?.channelId;
  state.config.ads.gdprConsent = windowNamespace?.ads?.gdprConsent;
  state.config.ads.contentTier = windowNamespace?.ads?.contentTier;
  state.config.ads.keywords = windowNamespace?.ads?.keywords;
  state.config.ads.placement = windowNamespace?.ads?.placement;
  state.config.ads.trackColor = windowNamespace?.ads?.trackColor;
  state.config.ads.runAdEvery = windowNamespace?.ads?.runAdEvery || 3;

  // RSS
  state.config.rss.enabled = windowNamespace?.rss?.enabled;
  state.config.rss.url = windowNamespace?.rss?.url;
  // customClasses
  state.config.customClasses.launcherIcon = windowNamespace?.customClasses?.launcherIcon;
  state.config.customClasses.players = windowNamespace?.customClasses?.players;

  setCustomDictionnary(windowNamespace);

  state.config.informationTooltip.enabled = windowNamespace?.informationTooltip?.enabled;
  state.config.informationTooltip.text =
    windowNamespace?.informationTooltip?.text ||
    _translate.getTranslation(currentLang, 'INFORMATIVE_MESSAGE_PLAYER_FEATURE') ||
    'Cette nouvelle fonctionnalit&eacute; utilise une voix synth&eacute;tique g&eacute;n&eacute;r&eacute;e par ordinateur. Il peut y avoir des erreurs, par exemple dans la prononciation, le sentiment et le ton';

  // Tracks
  state.config.tracks.article.color = windowNamespace?.tracks?.article?.color;
  state.config.tracks.article.backgroundColor = windowNamespace?.tracks?.article?.backgroundColor;

  // Labels
  state.config.labels.listenArticle.text = windowNamespace?.labels?.listenArticle?.text;
  state.config.labels.listenArticle.color = windowNamespace?.labels?.listenArticle?.color;
  state.config.labels.listenArticle.colorHover = windowNamespace?.labels?.listenArticle?.colorHover;
  state.config.labels.listenArticle.font = windowNamespace?.labels?.listenArticle?.font;
  state.config.labels.credits.enabled = windowNamespace?.labels?.credits?.enabled;
  state.config.labels.credits.text = windowNamespace?.labels?.credits?.text;
  state.config.labels.credits.color = windowNamespace?.labels?.credits?.color;

  // Buttons
  state.config.buttons.play.color = windowNamespace?.buttons?.play?.color;
  state.config.buttons.play.align = windowNamespace?.buttons?.play?.align;
  state.config.buttons.play.radius = windowNamespace?.buttons?.play?.radius;
  state.config.buttons.play.icon = windowNamespace?.buttons?.play?.icon;
}

function setCustomDictionnary(windowNamespace) {
  // Boucle dans le dico
  const defaultInformationTextKey = {
    article: 'LISTEN_ARTICLE',
    ad: 'CONTENT_AFTER_AD',
  };
  for (const lang in state.config.informationText) {
    for (const field in state.config.informationText[lang]) {
      state.config.informationText[lang][field] =
        windowNamespace?.informationText?.[lang][field] || _translate.getTranslation(lang, defaultInformationTextKey[field]);
    }
  }
}

export default initStore;
